import styled from 'styled-components';
import {ClearIcon} from '@/components/Icons';
import {Typography} from '@/components/Typography';
import {MAP_LAYER_Z_INDEX} from '@/map/constants';
import {calcBicycleSpeed, calcWalkingSpeed, distanceToString} from '@/map/utils';
import {Flexbox, FlexFullGrow} from '@/styled-components';
import {palette} from '@/styles';
import {Marker} from '../../Overlay';
import {RulerInfoCursorOptions} from './index.d';

interface IProps extends naver.maps.MarkerOptions, IContentProps {}

interface IContentProps {
  id?: number;
  distance?: number;
  showEscapeInfo?: boolean;
  options?: RulerInfoCursorOptions;
  onRemove?(id?: number): void;
}

export const InfoCursor = ({
  id,
  distance,
  showEscapeInfo,
  options = {},
  onRemove,
  ...props
}: IProps) => {
  return (
    <Marker
      {...props}
      clickable={false}
      draggable={false}
      zIndex={MAP_LAYER_Z_INDEX.MAP_CURSOR}
    >
      <Content
        id={id}
        distance={distance}
        showEscapeInfo={showEscapeInfo}
        options={options}
        onRemove={onRemove}
      />
    </Marker>
  );
};

const getTimeHtml = (min: number = 0) => {
  let t = '';
  const h = Math.floor(min / 60);
  const m = min % 60;

  if (h > 0) {
    t += `<strong>${h}</strong>시간 `;
  }
  if (m > 0 || h <= 0) {
    t += `<strong>${m}</strong>분`;
  }

  return t.trim();
};

const Content = ({
  id,
  distance = 0,
  showEscapeInfo,
  options,
  onRemove,
}: IContentProps) => {
  const handleRemove = () => onRemove?.(id);

  return (
    <Root>
      {!!onRemove && id && id > 0 && (
        <button onClick={handleRemove}>
          <img src={ClearIcon} width="16" height="16" alt="삭제" />
        </button>
      )}
      {(!options || !options.hideTotalDistance) && (
        <Section>
          <Flexbox>
            <Typography>총 거리</Typography>
            <FlexFullGrow />
            <SpeedText>
              <strong>
                {distanceToString(distance ?? 0, {hideUnit: true})}
              </strong>
              {distance < 1000 ? 'm' : 'km'}
            </SpeedText>
          </Flexbox>
        </Section>
      )}
      {(!options || !options.hideWalkSpeed) && (
        <Section>
          <Flexbox>
            <Typography>도보</Typography>
            <FlexFullGrow />
            <SpeedText
              dangerouslySetInnerHTML={{
                __html: getTimeHtml(calcWalkingSpeed(distance)),
              }}
            />
          </Flexbox>
        </Section>
      )}
      {(!options || !options.hideBicycleSpeed) && (
        <Section>
          <Flexbox>
            <Typography>자전거</Typography>
            <FlexFullGrow />
            <SpeedText
              dangerouslySetInnerHTML={{
                __html: getTimeHtml(calcBicycleSpeed(distance)),
              }}
            />
          </Flexbox>
        </Section>
      )}
      {showEscapeInfo && (
        <Section>마우스 오른쪽 버튼 또는 'esc'키를 눌러 마침</Section>
      )}
    </Root>
  );
};

const Root = styled(Typography).attrs({
  as: 'div',
  variant: 'medium12',
  color: palette.gray600,
})`
  width: 132px;
  margin-left: 8px;
  border-radius: 5px;
  background-color: ${palette.white};
  border: 1px solid ${palette.gray300};
  padding: 0 8px;
  position: relative;
  font-weight: 500;
  button {
    cursor: pointer;
    position: absolute;
    right: -6px;
    top: -6px;
  }
`;

const SpeedText = styled.div`
  strong {
    color: ${palette.teal100};
  }
  color: ${palette.gray800};
`;

const Section = styled.div`
  padding: 8px 0;
  & + & {
    border-top: solid 1px ${palette.gray200};
  }
`;
